/* eslint-disable react/no-unused-class-component-methods */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable no-unused-vars, react/default-props-match-prop-types, react/require-default-props, react/sort-comp, no-unused-expressions, react/prop-types */
import React from 'react';
import { keyCode as KEY_CODES } from '@bingads-webui/keycode';
import {
  uniqueId, noop, first, last,
} from 'underscore';
import {
  getTabbableNodes,
  getFrontGuards,
  addDataFocus,
  removeDataFocus,
  focusAsync,
} from './focus-utils';

export interface IFocusLockProps extends Pick<React.HTMLAttributes<HTMLDivElement>, 'children' | 'className' | 'tabIndex'> {
  onExit?: React.KeyboardEventHandler<HTMLDivElement>;
  isControlledFocusLock?: boolean;
  isFocusLock?: boolean;
  lockRegionAriaLabel?: string;
  focusFirstItemOnMount?: boolean;
  skipFocusOnMount?: boolean;
  getAnchor?: (() => HTMLElement | null) | null;
}

export class FocusLock extends React.PureComponent<IFocusLockProps> {
  static defaultProps = {
    className: 'react-focus-lock',
    onExit: noop,
    isControlledFocusLock: false,
    isFocusLock: false,
    lockRegionAriaLabel: '',
    focusFirstItemOnMount: false,
    skipFocusOnMount: false,
    getAnchor: null as IFocusLockProps['getAnchor'],
    tabIndex: 0,
  };

  // Shared instance properties
  private rootRef: React.RefObject<HTMLDivElement>;

  private anchor: HTMLElement | null;

  private focusId: string;

  private firstFocusNode: HTMLElement | null;

  private lastFocusNode: HTMLElement | null;

  private isFocusLockStart: boolean;

  constructor(props: IFocusLockProps) {
    super(props);
    this.rootRef = React.createRef<HTMLDivElement>();
    this.anchor = null;
    this.focusId = uniqueId('focus_id_');
    this.firstFocusNode = null;
    this.lastFocusNode = null;
    this.isFocusLockStart = false;
  }

  componentDidMount() {
    // if not customized trigger focus, will trigger it when did mount component
    if (!this.props.isControlledFocusLock) {
      this.anchor = document && (document.activeElement as HTMLElement);
      this.focusOneItemOnMount();
    }
  }

  componentDidUpdate(prevProps: IFocusLockProps) {
    const { isControlledFocusLock, isFocusLock } = this.props;

    if (prevProps.isControlledFocusLock === isControlledFocusLock && prevProps.isFocusLock === isFocusLock) {
      return;
    }

    if (isControlledFocusLock) {
      if (isFocusLock) {
        this.anchor = document && (document.activeElement as HTMLElement);
        this.focusOneItemOnMount();
      } else {
        removeDataFocus(this.rootRef.current);
        this.returnFocus();
        this.isFocusLockStart = false;
      }
    }
  }

  componentWillUnmount() {
    if (!this.props.isControlledFocusLock) {
      this.returnFocus();
    }
    this.anchor = null;
    this.firstFocusNode = null;
    this.lastFocusNode = null;
    this.focusId = '';
  }

  onFocusFront = (event: React.FocusEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    this.setFirstFocusNode();
    if (this.firstFocusNode) {
      this.firstFocusNode.focus();
    }
  };

  // onFocus Back is only handled for front guard focusable situation
  onFocusBack = (event: React.FocusEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // skip the first time to focus back when initial focus lock
    this.setLastFocusNode();
    if (this.lastFocusNode && this.isFocusLockStart) {
      this.lastFocusNode.focus();
    } else if (this.lastFocusNode) {
      this.isFocusLockStart = true;
    }
  };

  setFirstFocusNode = () => {
    const tabbableNodes = getTabbableNodes(this.rootRef.current);
    this.firstFocusNode = tabbableNodes && tabbableNodes.length > 0 ? first(tabbableNodes) : null;
  };

  setLastFocusNode = () => {
    const tabbableNodes = getTabbableNodes(this.rootRef.current);
    this.lastFocusNode = tabbableNodes && tabbableNodes.length > 0 ? last(tabbableNodes) : null;
  };

  focusOneItemOnMount = () => {
    const { skipFocusOnMount, focusFirstItemOnMount } = this.props;
    if (!skipFocusOnMount) {
      this.setFirstFocusNode();

      if (focusFirstItemOnMount && this.firstFocusNode) {
        focusAsync(this.firstFocusNode, () => addDataFocus(this.rootRef.current));
        this.isFocusLockStart = true;
      } else {
        this.focusFrontGuard();
      }
    } else {
      this.isFocusLockStart = true;
    }
  };

  focusFrontGuard = () => {
    const frontGuard = getFrontGuards(this.rootRef.current, this.focusId);
    focusAsync(frontGuard, () => addDataFocus(this.rootRef.current));
  };

  returnFocus = () => {
    if (this.props.getAnchor && this.props.getAnchor()) {
      // replace the anchor if set it outside
      this.anchor = this.props.getAnchor();
    }
    focusAsync(this.anchor, null, true);
  };

  focusNextElement = (currentElement: HTMLElement) => {
    const tabbableElements = getTabbableNodes(this.rootRef.current);
    const currentIndex = tabbableElements.indexOf(currentElement);

    if (currentIndex !== -1 && currentIndex < tabbableElements.length - 1) {
      tabbableElements[currentIndex + 1].focus();
    } else if (currentIndex === tabbableElements.length - 1) {
      tabbableElements[0].focus();
    }
  };

  exitHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.which === KEY_CODES.Escape) {
      this.props.onExit && this.props.onExit(event);
    }
  };

  render() {
    const {
      className, children, lockRegionAriaLabel, tabIndex,
    } = this.props;

    return (
      <div className={className} ref={this.rootRef} onKeyDown={this.exitHandler}>
        <div id={`guard-front-${this.focusId}`} data-focus-guard tabIndex={tabIndex} aria-label={lockRegionAriaLabel} onFocus={this.onFocusBack} role="region" />
        {children}
        <div id={`guard-tail-${this.focusId}`} data-focus-guard tabIndex={tabIndex} onFocus={this.onFocusFront} />
      </div>
    );
  }
}
